import React, { useState } from "react";
import axios from "axios";
import { ImagePlus, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 5;
const MAX_BYTES = 8 * 1024 * 1024;

export const PhotoUploader = ({ photos, setPhotos }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onFiles = async (e) => {
    const files = [...e.target.files].slice(0, MAX_PHOTOS - photos.length);
    e.target.value = "";
    setError("");
    for (const file of files) {
      if (file.size > MAX_BYTES) {
        setError(`${file.name} is over 8 MB`);
        continue;
      }
      setBusy(true);
      try {
        const body = new FormData();
        body.append("file", file);
        const res = await axios.post(`${API}/uploads/photo`, body);
        setPhotos((prev) => [...prev, { id: res.data.id, preview: URL.createObjectURL(file), name: file.name }]);
      } catch (err) {
        setError(err?.response?.data?.detail || "Upload failed, please try again");
      } finally {
        setBusy(false);
      }
    }
  };

  const remove = (id) => setPhotos((prev) => prev.filter((p) => p.id !== id));

  return (
    <div className="space-y-2" data-testid="quote-photo-uploader">
      <span className="text-sm font-semibold text-slate-700">Property photos (optional, up to {MAX_PHOTOS})</span>
      <div className="flex flex-wrap gap-3">
        {photos.map((p) => (
          <div key={p.id} className="relative h-20 w-20 overflow-hidden rounded-lg border border-slate-200" data-testid="quote-photo-thumb">
            <img src={p.preview} alt={`Uploaded property photo ${p.name}`} className="h-full w-full object-cover" />
            <button type="button" onClick={() => remove(p.id)} aria-label={`Remove ${p.name}`} data-testid="quote-photo-remove" className="absolute right-1 top-1 rounded-full bg-white/90 p-0.5 text-slate-700 hover:text-red-600">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {photos.length < MAX_PHOTOS && (
          <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-slate-300 text-xs font-medium text-slate-500 transition-colors hover:border-sky-500 hover:text-sky-600">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
            {busy ? "Uploading" : "Add photo"}
            <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={onFiles} disabled={busy} className="sr-only" data-testid="quote-photo-input" />
          </label>
        )}
      </div>
      {error && <p className="text-xs font-medium text-red-600" data-testid="quote-photo-error">{error}</p>}
      <p className="text-xs text-slate-500">Photos help us give an accurate price. JPG, PNG or WEBP, max 8 MB each.</p>
    </div>
  );
};
